import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { consoleError } from '../log';
import showNotification from '../components/extras/showNotification';
import React from 'react';
import { t } from 'i18next';
import { v4 as uuidv4 } from 'uuid';
import { getLoggedUserId } from '../common/function/utilities';
import { CHECK_LOGIN, JWT } from '../constants/auth.constants';
import { CACHE_CONTROL, CACHE_CONTROL_VALUE, CLIENT_ID as CLIENT_ID, CONTENT_TYPE, CONTENT_TYPE_APPLICATION_JSON, CONTENT_TYPE_MULTIPART_FORM_DATA, EXPIRES, EXPIRES_VALUE, LANGUAGE as LANGUAGE, PRAGMA, PRAGMA_VALUE, X_REQUEST_ID } from '../constants/headers.constants';
import { CLIENT_ID as LS_CLIENT_ID, LANGUAGE as LS_LANGUAGE } from '../constants/localstorage.constants';
import { ALERTS_COUNT, AUTH_REFRESH_TOKEN, axiosRefreshToken } from '../utils/axios';
import { ILoginResponse } from '../common/io/login.response';
import { getFromLocalStorage, getFromSessionStorage, setToSessionStorage } from '../utils/storage.util';

const handleError = (removeLoading: Function, error: any): void => {
	removeLoading();

	if (error) {
		if (error === 'TOKEN_EXPIRED') {
			showNotification(<></>, <h5>Token expired</h5>, 'danger');
			localStorage.clear();
			return;
		}

		if (!error.response) {
			showNotification(<></>, <h5>Error</h5>, 'danger');
		} else if (error.response?.status === 401) {
			showNotification(<></>, <h5>{t('error.unauthorized')}</h5>, 'danger');
		} else if (error.response?.status === 404) {
			showNotification(<></>, <h5>{t('error.not-found')}</h5>, 'danger');
		} else if (error.response?.status === 422) {
			showNotification(<></>, <h5>{t('error.format-not-valid')}</h5>, 'danger');
		} else {
			showNotification(<></>, <h5>{t('error.general')}</h5>, 'danger');
		}

		if (error.response) {
			consoleError('Error ' + error.response.status + ' ' + error.response.statusText + ' =============> ', error.response);
		}
	}
};

export function requestInterceptor(addLoading: Function, removeLoading: Function): void {
    axios.interceptors.request.use(
        async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
            if (!config.url?.includes(ALERTS_COUNT)) {
				addLoading();
            }

            let accessToken: string | null | undefined = getFromLocalStorage(JWT);
            const checkLogin: string | null = getFromSessionStorage(CHECK_LOGIN);
            if (accessToken) {
                if (!config.url?.includes(AUTH_REFRESH_TOKEN) && checkLogin !== 'true') {
                    const loginResponse: ILoginResponse | null = await axiosRefreshToken();
                    if (loginResponse) {
                        accessToken = loginResponse.accessToken;
                    }
                    setToSessionStorage(CHECK_LOGIN, 'true');
                }
                config.headers.Authorization = `Bearer ${accessToken}`;
            }

            const language: string | null = getFromLocalStorage(LS_LANGUAGE);
            if (language) {
                config.headers[LANGUAGE] = language;
            }

            if (config.data instanceof FormData) {
                config.headers[CONTENT_TYPE] = CONTENT_TYPE_MULTIPART_FORM_DATA;
            } else {
                config.headers[CONTENT_TYPE] = CONTENT_TYPE_APPLICATION_JSON;
            }

            config.headers[CACHE_CONTROL] = CACHE_CONTROL_VALUE
            config.headers[PRAGMA] = PRAGMA_VALUE;
            config.headers[EXPIRES] = EXPIRES_VALUE;

            let clientId: string | null = getFromSessionStorage(LS_CLIENT_ID);
            if (!clientId) {
                clientId = (getLoggedUserId() ? getLoggedUserId() + "_" : "") + uuidv4();
                setToSessionStorage(LS_CLIENT_ID, clientId);
            }
            config.headers[CLIENT_ID] = clientId;
            config.headers[X_REQUEST_ID] = uuidv4();

            return config;
        },
        (err: any): Promise<never> => {
            handleError(removeLoading, err);
            return Promise.reject(err);
        }
    );
}

export function responseInterceptor(removeLoading: Function): void {
    axios.interceptors.response.use(
        (response: AxiosResponse): AxiosResponse => {
            removeLoading();
            return response;
        },
        (err: any): Promise<never> => {
            handleError(removeLoading, err);
            return Promise.reject(err);
        }
    );
}
